import { Box, Stack } from "@mui/material";
import { ReactNode, useEffect, useState } from "react";

import { useProtectedAxiosInstance } from "../../../hooks/axiosInstance";
import CustomTable from "../../common/tables/Table";

import { useNotificationContext } from "../../../context/notificationContext";
import errorResolver from "../../../utils/error-resolver";
import useTableSchema from "../../../hooks/useTableSchema";

import {
  FilterOperatorEnum,
  SortDirectionEnum,
} from "../../../services/Schemas/ListQueryString";

import EditAction from "../../common/edit-action/EditAction";
import { useUrl } from "../../../hooks/urlEncoding";

import {
  PartnerListColumnSortable,
  PartnersListRequest,
} from "../../../services/Endpoints/partners/PartnerEndpoint";
import InfoBadge, { OriginEnum } from "../../common/info-badge/InfoBadge";
import { PartnerSchema } from "../../../services/Schemas/Partner";
import DeleteAction from "../../common/delete-action/DeleteAction";
import { dateFormatter } from "../../../utils/date-formatter";
import LinkWithPopover from "../common/LinkWithPopover";
import { routerPaths } from "../../../constants/Paths";
import { Link, useLocation } from "react-router-dom";
import {
  CallDetailListColumnFilterable,
  CallDetailListColumnSortable,
} from "../../../services/Endpoints/broadcast/PbxCallDetails";
import { CallCalling, DocumentText } from "iconsax-react";
import { useCanAccess } from "../../../hooks/canAccess";
import { AccessEnums } from "../../../constants/accessEnum";
import { ITableSchema } from "../../../utils/types";
import NumberFilter from "../../common/tables/filters/NumberFilter";
import TextFilter from "../../common/tables/filters/TextFilter";
import DateTimeFilter from "../../common/tables/filters/DateTimeFilter";
import SelectionFilter from "../../common/tables/filters/SelectionFilter";
import {
  LogListColumnFilterable,
  LogListColumnSortable,
} from "../../../services/Endpoints/logs/LogListEndpoints";
import useUrlSort from "../../../hooks/useUrlSort";
import ViewActionLink from "../../common/view-action/ViewActionLink";

export interface IUsers extends PartnerSchema {
  status: ReactNode;
  operations: ReactNode | string;
}

const statusOptions = {
  active: "فعال",
  inactive: "غیر فعال",
};
export interface ISupportTabsComponent {
  walletReload: boolean;
  setWalletReload: (payload: boolean) => void;
  setPartnerInfo?: (payload: Partial<PartnerSchema>) => void;
  setOpenModal: (payload: boolean) => void;
}

export default function PartnerTableComponent({
  walletReload,
  setWalletReload,
  setPartnerInfo,
  setOpenModal,
}: Readonly<ISupportTabsComponent>) {
  const { parseQueryString, queryStringUrl, setParamWithSearchParams } =
    useUrl();
  const parsed = parseQueryString();
  const initialPage = parsed.page ? Number(parsed.page) : 1;
  const initialRows = parsed.count ? Number(parsed.count) : 10;
  const [page, setPage] = useState(initialPage);
  const [rowsPerPage, setRowsPerPage] = useState(initialRows);
  const [dataTransaction, setDataTransaction] = useState<IUsers[] | []>([]);
  const { can } = useCanAccess();

  const [total, setTotal] = useState(0);
  const [waiting, setWaiting] = useState(false);

  const protectedInstance = useProtectedAxiosInstance();
  const { setNotification } = useNotificationContext();
  const location = useLocation();
  const { parseUrlSorts, applyUrlSorts } = useUrlSort();
  const sorts = parseUrlSorts();

  const handleView = (payload: Partial<PartnerSchema>) => {
    setOpenModal(true);
    setPartnerInfo && setPartnerInfo(payload);
  };
  const handleDelete = async (id: string | number) => {
    const confirmDelete = window.confirm("آیا از حذف این توکن مطمئن هستید؟");
    if (!confirmDelete) return;
    try {
      setWaiting(true);
      const walletListRequest = new PartnersListRequest(protectedInstance);
      const resp = await walletListRequest.remove(id as string);
      setWalletReload(!walletReload);
      setWaiting(false);
    } catch (error) {
      errorResolver({ error, setNotification });
      setWaiting(false);
      console.error(error);
    }
  };

  const getDataTransaction = async () => {
    try {
      setWaiting(true);
      const walletListRequest = new PartnersListRequest(protectedInstance);
      const resp = await walletListRequest.list({
        page,
        count: rowsPerPage,
        ...parseQueryString(),
      });
      setTotal(resp.data.total);
      const transactions = resp.data.partners.map(
        ({
          caption,
          created_at,
          status,
          support_fullname,
          support_phone,
          updated_at,
          id,
          max_pending_calls
        }) => ({
          id,
          created_at,
          max_pending_calls,
          caption,
          support_fullname,
          support_phone,
          updated_at,
          status: (
            <InfoBadge
              origin={OriginEnum.PARTNER}
              status={status as "active"}
            />
          ),
          operations: (
            <Stack justifyContent="center" alignItems="center" direction="row">
              <ViewActionLink
                disabled={!can(AccessEnums.AdminPartnerResetStatus)}
                link={`/${routerPaths.PARTNERS}/${id}`}
              />
              <DeleteAction
                disabled={!can(AccessEnums.AdminPartnerDelete)}
                handleDelete={() => handleDelete(id)}
              />
              <EditAction
                disabled={!can(AccessEnums.AdminPartnerUpdate)}
                handleEdit={() =>
                  handleView({
                    id,
                    caption,
                    status,
                    support_fullname,
                    support_phone,
                    max_pending_calls
                  })
                }
              />
              <LinkWithPopover
                disabled={!can(AccessEnums.AdminPbxCallDetailRecordsList)}
                title="تماس ها"
              >
                <Stack
                  sx={{
                    padding: "4px",
                    borderRadius: "50%",
                    width: "fit-content",
                    cursor: "pointer",
                    color: (theme) => theme.palette.complement[400],
                    "&:hover": {
                      color: (theme) => theme.palette.others.link,
                      // backgroundColor: (theme) => theme.palette.others.doneBg,
                    },
                  }}
                  component={Link}
                  to={queryStringUrl(routerPaths.PBX_CALL_DETAIL, {
                    filters: {
                      [CallDetailListColumnFilterable.PARTNER_ID]: {
                        column: CallDetailListColumnFilterable.PARTNER_ID,
                        operator: FilterOperatorEnum.EQUAL,
                        value: id,
                      },
                    },
                    sorts: {
                      [CallDetailListColumnSortable.ID]: {
                        column: CallDetailListColumnSortable.ID,
                        direction: SortDirectionEnum.DESC,
                      },
                    },
                  })}
                >
                  <CallCalling size="20" />
                </Stack>
              </LinkWithPopover>
              <LinkWithPopover
                title="لاگ ها"
                disabled={!can(AccessEnums.AdminLogsList)}
              >
                <Stack
                  sx={{
                    padding: "4px",
                    borderRadius: "50%",
                    width: "fit-content",
                    cursor: "pointer",
                    color: (theme) => theme.palette.complement[400],
                    "&:hover": {
                      color: (theme) => theme.palette.others.link,
                      // backgroundColor: (theme) => theme.palette.others.doneBg,
                    },
                  }}
                  component={Link}
                  to={queryStringUrl(routerPaths.LOGS, {
                    filters: {
                      [LogListColumnFilterable.PARTNER_ID]: {
                        column: LogListColumnFilterable.PARTNER_ID,
                        operator: FilterOperatorEnum.EQUAL,
                        value: id,
                      },
                    },
                    sorts: {
                      [LogListColumnSortable.ID]: {
                        column: LogListColumnSortable.ID,
                        direction: SortDirectionEnum.DESC,
                      },
                    },
                  })}
                >
                  <DocumentText size="20" />
                </Stack>
              </LinkWithPopover>
            </Stack>
          ),
        })
      );
      setDataTransaction(transactions);
      setWaiting(false);
    } catch (error) {
      errorResolver({ error, setNotification });
      setWaiting(false);
      console.error(error);
    }
  };

  useEffect(() => {
    getDataTransaction();
  }, [walletReload, page, rowsPerPage, location.search]);

  const partnersColumns: ITableSchema[] = [
    {
      key: "id",
      label: "شناسه",
      minWidth: 100,
      align: "center",
      FilterComponent: NumberFilter,
      sortable: true,
    },
    {
      key: "caption",
      label: "نام",
      minWidth: 170,
      align: "center",
      FilterComponent: TextFilter,
    },
    {
      key: "support_phone",
      label: "شماره تماس",
      minWidth: 170,
      align: "center",
      FilterComponent: TextFilter,
    },
    {
      key: "support_fullname",
      label: "نام و نام خانوادگی",
      minWidth: 170,
      align: "center",
      FilterComponent: TextFilter,
    },
    {
      key: "max_pending_calls",
      label: "حداکثر تماس در حال انتظار",
      minWidth: 120,
      align: "center",
      FilterComponent: NumberFilter,
    },
    {
      key: "updated_at",
      label: "تاریخ ویرایش",
      minWidth: 170,
      align: "center",
      format: (value) => value && dateFormatter({ lang: "fa", date: value }),
      FilterComponent: DateTimeFilter,
    },
    {
      key: "created_at",
      label: "تاریخ ایجاد",
      minWidth: 170,
      align: "center",
      format: (value) => value && dateFormatter({ lang: "fa", date: value }),
      FilterComponent: DateTimeFilter,
    },
    {
      key: "status",
      label: "وضعیت",
      minWidth: 170,
      align: "center",
      FilterComponent: SelectionFilter,
      options: statusOptions,
    },
    {
      key: "operations",
      label: "عملیات",
      minWidth: 170,
      align: "center",
    },
  ];
  const { filtersSchema, columns } = useTableSchema(partnersColumns);

  const handleChangePage = (newPage: number) => {
    setPage(newPage);
    setParamWithSearchParams("page", String(newPage));
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newRows = +event.target.value;
    setRowsPerPage(newRows);
    setParamWithSearchParams("count", String(newRows));
  };

  return (
    <Box sx={{ width: "100%", typography: "body1", backgroundColor: "white" }}>
      <Box
        sx={{ borderBottom: 1, borderColor: "divider", width: "100%" }}
      ></Box>

      <CustomTable
        reloadAction={() => setWalletReload(!walletReload)}
        hasWaiting={waiting}
        hasSearchFilter
        hasBottomPagination
        hasTopPagination
        columns={columns}
        count={total}
        handleChangePage={handleChangePage}
        handleChangeRowsPerPage={handleChangeRowsPerPage}
        page={page}
        rows={dataTransaction}
        rowsPerPage={rowsPerPage}
        filtersSchema={filtersSchema}
        onApplySorts={applyUrlSorts}
        sorts={sorts}
      />
    </Box>
  );
}
